/**
 * Iter 240 (Lot 4 polish) — Carte Pointage CallMeBot WhatsApp dans Settings > API.
 *
 * Twilio a été remplacé par CallMeBot (per-user apikey). Cette carte sert
 * désormais à :
 * - Configurer les paramètres GLOBAUX du pointage (rayon géofence, validation manager)
 * - Afficher le statut : combien d'employés ont configuré leur apikey CallMeBot
 * - Rediriger les employés vers /mon-espace/whatsapp pour configurer leur clé
 */
import { useEffect, useState, useCallback } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Loader2, MessageCircle, CheckCircle2, AlertCircle, ExternalLink, Clock, ArrowRight, Users } from "lucide-react";
import { toast } from "sonner";

export default function TimeTrackingCard() {
  const { axiosAuth } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    default_geofence_radius_m: 100,
    default_country_prefix: "+33",
    require_geofence: false,
    manager_approval_outside_zone: true,
  });

  const load = useCallback(async () => {
    try {
      const api = axiosAuth();
      const r = await api.get("/tracking/status");
      setStatus(r.data);
      setForm((prev) => ({
        ...prev,
        default_geofence_radius_m: r.data.default_geofence_radius_m || 100,
        require_geofence: !!r.data.require_geofence,
        manager_approval_outside_zone: r.data.manager_approval_outside_zone !== false,
      }));
    } catch (e) {
      console.error("tracking status failed", e);
    } finally {
      setLoading(false);
    }
  }, [axiosAuth]);

  useEffect(() => { load(); }, [load]);

  const saveConfig = async () => {
    setSaving(true);
    try {
      const api = axiosAuth();
      await api.put("/tracking/config", form);
      toast.success("Paramètres pointage enregistrés");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="bg-[#14101c] border border-white/5 rounded-xl p-5 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-[#f7c935]" /></div>;
  }

  const employeesConfigured = status?.employees_configured || 0;
  const totalEmployees = status?.total_employees || 0;
  const ratio = totalEmployees > 0 ? Math.round((employeesConfigured / totalEmployees) * 100) : 0;
  const isHealthy = employeesConfigured > 0;

  return (
    <div className="bg-[#14101c] border border-white/5 rounded-xl p-5" data-testid="time-tracking-card">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg bg-emerald-500/15 flex items-center justify-center flex-shrink-0">
          <MessageCircle className="w-5 h-5 text-emerald-400" />
        </div>
        <div className="flex-1">
          <div className="flex items-center justify-between gap-2 mb-1">
            <h3 className="text-sm font-bold text-white">Pointage salariés (WhatsApp via CallMeBot)</h3>
            {isHealthy ? (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 font-bold uppercase flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3" /> Actif
              </span>
            ) : (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 font-bold uppercase flex items-center gap-1">
                <AlertCircle className="w-3 h-3" /> À configurer
              </span>
            )}
          </div>
          <p className="text-xs text-slate-400">
            Les employés reçoivent leur code OTP de pointage par WhatsApp gratuit (CallMeBot). Aucun coût SMS.
          </p>
        </div>
      </div>

      {/* Stats employés configurés */}
      <div className="bg-[#0b0811] border border-white/5 rounded-lg p-3 mb-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Users className="w-3.5 h-3.5 text-cyan-400" />
            <span className="text-xs text-slate-300 font-bold">Employés ayant configuré leur apikey</span>
          </div>
          <span className="text-sm font-black text-white">{employeesConfigured}/{totalEmployees} ({ratio}%)</span>
        </div>
        <div className="w-full bg-[#14101c] rounded-full h-1.5 overflow-hidden">
          <div className="h-full bg-emerald-400 transition-all" style={{ width: `${ratio}%` }} />
        </div>
        {employeesConfigured < totalEmployees && (
          <p className="text-[11px] text-slate-500 mt-2">
            Les employés doivent activer CallMeBot dans <strong className="text-white">Mon Espace → Mon WhatsApp</strong>.
          </p>
        )}
      </div>

      {/* CTA */}
      <div className="flex flex-wrap gap-2 mb-4">
        <button
          onClick={() => navigate("/mon-espace/pointages")}
          className="flex items-center gap-1.5 text-xs bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300 px-3 py-1.5 rounded-lg font-bold border border-emerald-500/30"
          data-testid="my-whatsapp-btn"
        >
          <MessageCircle className="w-3.5 h-3.5" />
          Configurer WhatsApp & pointer
          <ArrowRight className="w-3 h-3" />
        </button>
        <a
          href="https://www.callmebot.com/blog/free-api-whatsapp-messages/"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1.5 text-xs bg-[#0b0811] hover:bg-[#1a1424] text-slate-300 px-3 py-1.5 rounded-lg font-bold border border-white/10"
        >
          <ExternalLink className="w-3.5 h-3.5" />
          Documentation CallMeBot
        </a>
      </div>

      {/* Paramètres globaux pointage */}
      <details className="group">
        <summary className="cursor-pointer text-xs text-slate-300 font-bold flex items-center gap-2 hover:text-white">
          <Clock className="w-3.5 h-3.5" />
          Paramètres globaux du pointage GPS
        </summary>
        <div className="space-y-3 mt-3 pl-5">
          <div>
            <label className="text-[11px] text-slate-400 mb-1 block">Rayon géofence par défaut (mètres)</label>
            <input
              type="number"
              value={form.default_geofence_radius_m}
              onChange={(e) => setForm({ ...form, default_geofence_radius_m: parseInt(e.target.value) || 100 })}
              className="w-32 bg-[#0b0811] border border-white/10 rounded-lg px-3 py-1.5 text-xs text-white"
              data-testid="geofence-radius-input"
            />
          </div>
          <div>
            <label className="text-[11px] text-slate-400 mb-1 block">Préfixe pays par défaut</label>
            <input
              type="text"
              value={form.default_country_prefix}
              onChange={(e) => setForm({ ...form, default_country_prefix: e.target.value })}
              className="w-32 bg-[#0b0811] border border-white/10 rounded-lg px-3 py-1.5 text-xs text-white"
              placeholder="+33"
            />
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.require_geofence}
              onChange={(e) => setForm({ ...form, require_geofence: e.target.checked })}
              className="rounded"
            />
            <span className="text-xs text-slate-300">Exiger d'être dans la géofence pour pointer (rejeter sinon)</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.manager_approval_outside_zone}
              onChange={(e) => setForm({ ...form, manager_approval_outside_zone: e.target.checked })}
              className="rounded"
            />
            <span className="text-xs text-slate-300">Demander validation manager si pointage hors zone</span>
          </label>
          <button
            onClick={saveConfig}
            disabled={saving}
            className="text-xs bg-[#f7c935] text-[#0b0811] hover:bg-[#e6b825] px-3 py-1.5 rounded-lg font-bold disabled:opacity-50"
            data-testid="save-config-btn"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin inline mr-1" /> : null}
            Enregistrer les paramètres
          </button>
        </div>
      </details>
    </div>
  );
}
